"use client"

import { PrimaryButton, SecondaryButton } from "@/components/buttons"
import { ProseContainer } from "@/components/common"
import Link from "next/link"
import { AnimatePresence, motion } from "framer-motion"
import Cookies from "js-cookie"
import { CONSENT_COOKIE } from "./constants"

declare global {
  interface Window {
    gtag: (
      command: "consent",
      action: "default" | "update",
      params: object
    ) => void
  }
}

/**
 * A cookie consent banner that slides in from the bottom of the screen.
 * It provides options for the user to accept or decline tracking cookies.
 * The component handles setting the consent cookie and signaling the user's
 * choice to Google Analytics.
 */
export function CookieBanner() {
  const handleAccept = () => {
    window.gtag("consent", "update", {
      ad_storage: "granted",
      analytics_storage: "granted",
    })
    Cookies.set(CONSENT_COOKIE, "true", { expires: 365 })
    // This will just set the cookie, the banner will disappear on next navigation
    // as the server will see the cookie and not render the banner.
    // For an instant removal, we'd need to manage state here.
    window.location.reload() // Simple solution for instant update
  }

  const handleDecline = () => {
    Cookies.set(CONSENT_COOKIE, "false", { expires: 365 })
    window.location.reload()
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: "0%" }}
        exit={{ y: "100%" }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className="fixed bottom-0 left-0 z-50 w-full"
      >
        <div className="mx-auto max-w-4xl px-4 pb-4">
          <div className="rounded-lg border border-gray-200 bg-white/80 p-6 shadow-2xl backdrop-blur-lg dark:border-neutral-700 dark:bg-neutral-800/80">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <ProseContainer className="text-center">
                We use cookies to enhance your browsing experience and analyze
                our traffic.
                <br />
                By clicking &quot;Accept All&quot;, you consent to our use of
                cookies. Read our{" "}
                <Link
                  href="/legal/privacy-policy"
                  className="underline hover:text-text-primary"
                >
                  Privacy Policy
                </Link>
                .
              </ProseContainer>
              <div className="flex flex-shrink-0 gap-4">
                <PrimaryButton buttonProps={{ onClick: handleAccept }}>
                  Accept All
                </PrimaryButton>
                <SecondaryButton buttonProps={{ onClick: handleDecline }}>
                  Decline
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
